import { motion } from 'framer-motion';
import { useState } from 'react';
import { FiPlus } from 'react-icons/fi';

const QuestionsItems = [
    {
        question: 'How secure is QCBT for conducting high-stakes exams?',
        answer: 'QCBT uses multiple layers of security including data encryption, tab locking technology, fullscreen mode enforcement, and AI-powered remote proctoring to ensure the highest level of exam integrity. Our platform has been tested and validated by cybersecurity experts and complies with international data protection standards.'
    },
    {
        question: 'Can I customize the exam interface to match our institution\'s branding?',
        answer: 'Yes, QCBT offers extensive customization options including your institution\'s logo, colors, and branding elements. You can also customize email templates, certificates, and result pages to provide a consistent experience for your students.'
    },
    {
        question: 'What types of questions can I include in my exams?',
        answer: 'QCBT supports multiple question types including multiple choice, true/false, short answer, essay, matching, fill-in-the-blank, and image-based questions. You can also include mathematical formulas, code snippets, and diagrams in your questions.'
    },
    {
        question: 'How does the hybrid examination mode work?',
        answer: 'Hybrid examination mode allows you to conduct the same exam simultaneously for both online (remote) and offline (test center) participants. The platform synchronizes data across both modes, providing consistent experiences and centralized reporting regardless of how students take the exam.'
    },
    {
        question: 'What happens if a student loses internet connection during an exam?',
        answer: 'QCBT has built-in offline capabilities that allow students to continue working on their exams even if they temporarily lose internet connection. Their responses are cached locally and automatically synchronized once the connection is restored, ensuring no work is lost.'
    },
    {
        question: 'Is technical support available during exams?',
        answer: 'Yes, we provide 24/7 technical support for critical exams. Students can access live chat support directly from the exam interface, and institutions can have dedicated support personnel assigned for important examination periods.'
    }
];

const Questions = () => {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const toggleQuestion = (index: number) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    return (
        <div className="w-full">
            <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                {/* Responsive header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="mb-6 md:mb-8 mt-4 md:mt-8 text-center relative"
                >
                    {/* Decorative elements - hidden on small screens */}
                    <motion.div
                        className="hidden sm:block absolute -top-6 -left-10 w-12 h-12 rounded-full bg-blue-100/30 blur-lg"
                        animate={{
                            scale: [1, 1.2, 1],
                            opacity: [0.4, 0.8, 0.4],
                            transition: {
                                duration: 8,
                                repeat: Infinity,
                                ease: "easeInOut",
                            },
                        }}
                    />

                    <h2 className="text-3xl sm:text-4xl text-gray-900 mb-4 font-heading uppercase">
                        <span className="font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-cyan-500">
                            Frequently Asked Questions
                        </span>{" "}
                        <span className="font-serif font-normal italic text-gray-600">
                            FAQ
                        </span>
                    </h2>

                </motion.div>

                {/* Responsive accordion */}
                <div className="space-y-2 sm:space-y-3">
                    {QuestionsItems.map((item, index) => (
                        <div
                            key={index}
                            className="relative overflow-hidden"
                        >
                            <button
                                onClick={() => toggleQuestion(index)}
                                className={`w-full flex items-start justify-between p-3 sm:p-4 text-left rounded-lg transition-all duration-300 shadow-inner cursor-pointer ${activeIndex === index
                                    ? 'bg-blue-50 border border-blue-200'
                                    : 'bg-white border border-gray-200 hover:border-blue-200'
                                    }`}
                            >
                                <h3 className={`text-sm sm:text-base font-medium ${activeIndex === index
                                    ? 'text-blue-800'
                                    : 'text-gray-800'
                                    } pr-3 sm:pr-4`}>
                                    {item.question}
                                </h3>
                                <div className={`p-1 rounded-full transition-all duration-300 ${activeIndex === index
                                    ? 'bg-blue-100 text-blue-600 rotate-45'
                                    : 'bg-gray-100 text-gray-500'
                                    }`}>
                                    <FiPlus className="h-3 w-3 sm:h-4 sm:w-4" />
                                </div>
                            </button>

                            <div
                                className={`transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${activeIndex === index
                                    ? 'max-h-96 opacity-100 mt-1 sm:mt-2'
                                    : 'max-h-0 opacity-0'
                                    }`}
                            >
                                <div className="relative pl-4 sm:pl-6 pr-4 sm:pr-8 pb-3 sm:pb-4 text-gray-600 text-xs sm:text-sm ml-2 sm:ml-3 text-justify">
                                    <div className="absolute top-0 left-0 h-full w-[2px] sm:w-[3px] bg-gradient-to-b from-sky-500 via-blue-600 to-indigo-700 rounded-sm"></div>
                                    <p>{item.answer}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default Questions;